import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Group-chat presence state, published by the chat session and consumed by the
 * top-right header row.
 *
 * The presence cluster has to render *inside* `FloatingHeaderControls` so the
 * collaborators sit in the same row as Share (the Miro treatment) — but that
 * component is global layout with no session context, and it lives in a sibling
 * subtree from the chat page. Bridging the two through a DOM portal slot
 * proved unreliable: the two subtrees commit in an order neither controls, so
 * the slot lookup raced the header's mount and the cluster silently never
 * appeared. Redux removes the ordering problem entirely — the chat page
 * publishes plain serialisable state, and the header renders from it whenever it
 * happens to mount.
 *
 * Only DATA lives here, never JSX: the store must stay serialisable, so the
 * header owns the markup and this slice owns the facts it renders from.
 */
interface GroupPresenceState {
  /** Session the state belongs to; null when the viewer isn't in a group chat. */
  sessionId: string | null;
  /** user_ids currently connected to the realtime channel (may include self). */
  presentUserIds: string[];
  /** user_ids currently typing (excludes self). */
  typingUserIds: string[];
  /** The viewer's own user_id, so the header can mark and sort their avatar. */
  selfUserId: string | null;
  /** True while @Lex is streaming a reply, which surfaces its avatar as live. */
  aiActive: boolean;
  /** Whether the members dialog is open — the header holds the trigger, the
   *  chat page renders the dialog, so the flag has to be shared. */
  membersOpen: boolean;
}

const initialState: GroupPresenceState = {
  sessionId: null,
  presentUserIds: [],
  typingUserIds: [],
  selfUserId: null,
  aiActive: false,
  membersOpen: false,
};

/** The presence facts the chat page publishes; `membersOpen` is toggled separately. */
type GroupPresencePayload = Omit<GroupPresenceState, 'membersOpen'>;

const groupPresenceSlice = createSlice({
  name: 'groupPresence',
  initialState,
  reducers: {
    /** Publish the current group session's presence (called on every change). */
    setGroupPresence: (state, action: PayloadAction<GroupPresencePayload>) => {
      state.sessionId = action.payload.sessionId;
      state.presentUserIds = action.payload.presentUserIds;
      state.typingUserIds = action.payload.typingUserIds;
      state.selfUserId = action.payload.selfUserId;
      state.aiActive = action.payload.aiActive;
    },
    /**
     * Clear on leaving a group session. Resets to `initialState` rather than
     * only nulling `sessionId`, so a stale roster can't flash in the header when
     * the next group session mounts.
     */
    clearGroupPresence: () => initialState,
    setMembersOpen: (state, action: PayloadAction<boolean>) => {
      state.membersOpen = action.payload;
    },
  },
});

export const { setGroupPresence, clearGroupPresence, setMembersOpen } =
  groupPresenceSlice.actions;

export default groupPresenceSlice.reducer;
